import { Fragment, useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { fmtDateTime } from '@em/shared';
import { api, qk, useSettings, useUsers } from '../../api';
import { useAuth } from '../../auth/AuthContext';
import { openDownload } from '../../components/format';
import { useToast } from '../../components/Toast';
import { Button, Card, EmptyState, ErrorAlert, Field, Input, Loading, Select } from '../../components/ui';

const PAGE = 100;
const OBJECT_TYPES = ['attendance_event', 'correction_request', 'exception', 'timesheet', 'pay_period', 'export_batch', 'employee', 'user_account', 'rule_template', 'roster_shift', 'roster', 'device', 'site', 'team', 'settings', 'payroll_category', 'public_holiday', 'privacy_notice', 'job'];

export function AuditLog() {
  const toast = useToast();
  const { can } = useAuth();
  const settings = useSettings();
  const users = useUsers(can('owner'));
  const tz = settings.data?.defaultTimezone ?? 'Australia/Sydney';
  const [objectType, setObjectType] = useState('');
  const [objectId, setObjectId] = useState('');
  const [actorId, setActorId] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [open, setOpen] = useState<Set<string>>(new Set());
  const filters = { objectType: objectType || undefined, objectId: objectId.trim() || undefined, actorId: actorId || undefined, from: from || undefined, to: to || undefined };

  const q = useInfiniteQuery({
    queryKey: qk.audit(filters),
    queryFn: ({ pageParam }) => api.audit({ ...filters, limit: PAGE, before: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => (last.length === PAGE ? last.at(-1)?.id : undefined),
  });
  const rows = q.data?.pages.flat() ?? [];
  const toggle = (id: string) =>
    setOpen((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  return (
    <Card
      title="Audit log"
      tight
      actions={
        can('owner') && (
          <Button size="sm" onClick={() => void openDownload('/api/audit.csv', toast.error)}>
            Download full log (CSV)
          </Button>
        )
      }
    >
      <div className="card-body" style={{ paddingBottom: 0 }}>
        <div className="filters">
          <Field label="Object type">
            <Select value={objectType} onChange={(e) => setObjectType(e.target.value)}>
              <option value="">Any</option>
              {OBJECT_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t.replace(/_/g, ' ')}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Object ID">
            <Input value={objectId} onChange={(e) => setObjectId(e.target.value)} placeholder="Exact ID" />
          </Field>
          {users.data && (
            <Field label="By user">
              <Select value={actorId} onChange={(e) => setActorId(e.target.value)}>
                <option value="">Anyone</option>
                {users.data.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </Select>
            </Field>
          )}
          <Field label="From">
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </Field>
          <Field label="To">
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </Field>
        </div>
      </div>
      {q.isPending ? (
        <Loading />
      ) : q.isError ? (
        <div className="card-body">
          <ErrorAlert error={q.error} />
        </div>
      ) : rows.length === 0 ? (
        <EmptyState title="No audit entries match" />
      ) : (
        <>
          <div className="table-wrap">
            <table className="table compact">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Who</th>
                  <th>Action</th>
                  <th>Object</th>
                  <th>IP</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((a) => (
                  <Fragment key={a.id}>
                    <tr>
                      <td className="nowrap small">{fmtDateTime(a.at, tz)}</td>
                      <td>
                        {a.actorName ?? '–'} <span className="muted small">({a.actorType})</span>
                      </td>
                      <td className="strong">{a.action}</td>
                      <td className="small">
                        {a.objectType.replace(/_/g, ' ')} <span className="mono muted">{a.objectId ?? ''}</span>
                      </td>
                      <td className="small mono">{a.ip ?? '–'}</td>
                      <td className="actions">
                        {(a.before != null || a.after != null) && (
                          <Button size="sm" variant="ghost" onClick={() => toggle(a.id)} aria-expanded={open.has(a.id)}>
                            {open.has(a.id) ? 'Hide' : 'Before / after'}
                          </Button>
                        )}
                      </td>
                    </tr>
                    {open.has(a.id) && (
                      <tr>
                        <td colSpan={6} style={{ background: 'var(--surface-2)' }}>
                          <div className="grid grid-2">
                            <div>
                              <div className="small muted">Before</div>
                              <pre className="json">{JSON.stringify(a.before, null, 2) ?? 'null'}</pre>
                            </div>
                            <div>
                              <div className="small muted">After</div>
                              <pre className="json">{JSON.stringify(a.after, null, 2) ?? 'null'}</pre>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
          <div className="toolbar" style={{ borderTop: '1px solid var(--border)', borderBottom: 0 }}>
            <span className="small muted">{rows.length} entries shown</span>
            <span className="spacer" />
            {q.hasNextPage && (
              <Button size="sm" onClick={() => void q.fetchNextPage()} loading={q.isFetchingNextPage}>
                Load older entries
              </Button>
            )}
          </div>
        </>
      )}
    </Card>
  );
}
